import { useMemo, useState } from "react";
import { useMutation, useQueries } from "@tanstack/react-query";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { ApiError, type LoyaltyStampRuleDto } from "@finora/api-client";
import { Button } from "@finora/ui";
import { api } from "../../lib/api";

const inputCls = "h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";

type Item = { product_id: string; variation_id: string | null };
type Product = { id: string; name: string };
interface Draft {
  id: string | null;
  name: string;
  stamps_required: number;
  is_active: boolean;
  items: Item[];
}

function errMsg(err: unknown, fallback: string) {
  if (err instanceof ApiError) {
    const m = err.response.message;
    return Array.isArray(m) ? m.join(" ") : m;
  }
  return fallback;
}

export function StampRules({ rules, products, onChanged }: { rules: LoyaltyStampRuleDto[]; products: Product[]; onChanged: () => void }) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [filter, setFilter] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const productName = (id: string) => products.find((p) => p.id === id)?.name ?? "—";

  // Varijacije svih artikala koji su izabrani u formi (učitavaju se po potrebi).
  const selectedProductIds = useMemo(() => [...new Set((draft?.items ?? []).map((x) => x.product_id))], [draft]);
  const variationQs = useQueries({
    queries: selectedProductIds.map((pid) => ({ queryKey: ["loyalty-variations", pid], queryFn: () => api.listProductVariations(pid) })),
  });
  const variationsOf = (pid: string) => variationQs[selectedProductIds.indexOf(pid)]?.data ?? [];

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return products.filter((p) => !q || p.name.toLowerCase().includes(q)).slice(0, 50);
  }, [products, filter]);

  const done = () => {
    setDraft(null);
    setErr(null);
    onChanged();
  };
  const save = useMutation({
    mutationFn: (d: Draft) => {
      const payload = { name: d.name, stamps_required: d.stamps_required, is_active: d.is_active, items: d.items };
      return d.id ? api.updateLoyaltyRule(d.id, payload) : api.createLoyaltyRule(payload);
    },
    onSuccess: done,
    onError: (e) => setErr(errMsg(e, "Čuvanje nije uspelo.")),
  });
  const toggle = useMutation({
    mutationFn: (r: LoyaltyStampRuleDto) => api.updateLoyaltyRule(r.id, { is_active: !r.is_active }),
    onSuccess: onChanged,
    onError: (e) => setErr(errMsg(e, "Izmena nije uspela.")),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.deleteLoyaltyRule(id),
    onSuccess: onChanged,
    onError: (e) => setErr(errMsg(e, "Brisanje nije uspelo.")),
  });

  // Označavanje: ceo artikal (sve varijacije) ili pojedinačne varijacije.
  const hasProduct = (pid: string) => !!draft?.items.some((x) => x.product_id === pid);
  const toggleProduct = (pid: string) => {
    if (!draft) return;
    setDraft({
      ...draft,
      items: hasProduct(pid)
        ? draft.items.filter((x) => x.product_id !== pid)
        : [...draft.items, { product_id: pid, variation_id: null }],
    });
  };
  const isAllVariations = (pid: string) => !!draft?.items.some((x) => x.product_id === pid && !x.variation_id);
  const hasVariation = (pid: string, vid: string) => !!draft?.items.some((x) => x.product_id === pid && x.variation_id === vid);
  const setAll = (pid: string) => {
    if (!draft) return;
    setDraft({ ...draft, items: [...draft.items.filter((x) => x.product_id !== pid), { product_id: pid, variation_id: null }] });
  };
  const toggleVariation = (pid: string, vid: string) => {
    if (!draft) return;
    const others = draft.items.filter((x) => x.product_id !== pid);
    let mine = draft.items.filter((x) => x.product_id === pid && x.variation_id);
    mine = mine.some((x) => x.variation_id === vid) ? mine.filter((x) => x.variation_id !== vid) : [...mine, { product_id: pid, variation_id: vid }];
    // Ako ništa nije ostalo, artikal ostaje izabran sa svim varijacijama.
    setDraft({ ...draft, items: [...others, ...(mine.length ? mine : [{ product_id: pid, variation_id: null }])] });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-slate-800">Pečati</div>
          <div className="text-xs text-slate-500">Svaki pečat ima svoje artikle i varijacije i svoj broj do besplatnog (npr. „10 kafa, 11. besplatna“).</div>
        </div>
        {!draft && (
          <Button onClick={() => setDraft({ id: null, name: "", stamps_required: 10, is_active: true, items: [] })}>
            <Plus className="mr-1 h-4 w-4" aria-hidden /> Dodaj pečat
          </Button>
        )}
      </div>

      {rules.length === 0 && !draft && <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-500">Još nema pečata. Dodajte prvi.</p>}

      {rules.map((r) => (
        <div key={r.id} className="rounded-lg border border-border p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-medium text-slate-900">
              {r.name} <span className="font-normal text-slate-500">· {r.stamps_required} pečata = 1 besplatno</span>
              {!r.is_active && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">isključen</span>}
            </div>
            <div className="flex items-center gap-1">
              <button className="rounded p-1.5 text-slate-600 hover:bg-slate-100" title="Izmeni" onClick={() => setDraft({ id: r.id, name: r.name, stamps_required: r.stamps_required, is_active: r.is_active, items: r.items })}>
                <Pencil className="h-4 w-4" aria-hidden />
              </button>
              <button className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100" onClick={() => toggle.mutate(r)}>
                {r.is_active ? "Isključi" : "Uključi"}
              </button>
              <button
                className="rounded p-1.5 text-red-600 hover:bg-red-50"
                title="Obriši"
                onClick={() => window.confirm(`Obrisati pečat „${r.name}“?`) && del.mutate(r.id)}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {r.items.map((x) => (
              <span key={`${x.product_id}|${x.variation_id}`} className="rounded-full bg-brand/10 px-2 py-0.5 text-xs text-brand">
                {productName(x.product_id)}
                {x.variation_id ? " (varijacija)" : " (sve varijacije)"}
              </span>
            ))}
          </div>
        </div>
      ))}

      {draft && (
        <div className="space-y-3 rounded-lg border border-brand/40 bg-brand/5 p-3">
          <div className="text-sm font-medium text-slate-900">{draft.id ? "Izmena pečata" : "Novi pečat"}</div>
          <div className="grid max-w-xl grid-cols-2 gap-3">
            <label className="block text-xs text-slate-500">
              Naziv (npr. Kafa)
              <input className={inputCls} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </label>
            <label className="block text-xs text-slate-500">
              Pečata do besplatnog
              <input className={inputCls} type="number" min={1} max={100} value={draft.stamps_required} onChange={(e) => setDraft({ ...draft, stamps_required: Math.max(1, Number(e.target.value) || 1) })} />
            </label>
          </div>

          <div className="text-xs text-slate-500">Artikli koji donose ovaj pečat ({draft.items.length} izabrano). Za artikal sa varijacijama možete izabrati samo neke.</div>
          {selectedProductIds.length > 0 && (
            <div className="space-y-2">
              {selectedProductIds.map((pid) => {
                const vars = variationsOf(pid);
                return (
                  <div key={pid} className="rounded-lg bg-surface p-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{productName(pid)}</span>
                      <button className="text-xs text-red-600" onClick={() => toggleProduct(pid)}>Ukloni</button>
                    </div>
                    {vars.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                        <label className="flex items-center gap-1 text-xs">
                          <input type="radio" checked={isAllVariations(pid)} onChange={() => setAll(pid)} /> Sve varijacije
                        </label>
                        {vars.map((v) => (
                          <label key={v.id} className="flex items-center gap-1 text-xs">
                            <input type="checkbox" checked={hasVariation(pid, v.id)} onChange={() => toggleVariation(pid, v.id)} /> {v.name}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="relative max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" aria-hidden />
            <input className={inputCls + " pl-9"} placeholder="Pretraži artikle za dodavanje" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </div>
          <div className="max-h-48 overflow-auto rounded-lg border border-border bg-surface">
            {shown.map((p) => (
              <label key={p.id} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50">
                <input type="checkbox" checked={hasProduct(p.id)} onChange={() => toggleProduct(p.id)} />
                {p.name}
              </label>
            ))}
            {shown.length === 0 && <p className="px-3 py-2 text-sm text-slate-400">Nema artikala.</p>}
          </div>

          {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
          <div className="flex gap-2">
            <Button disabled={save.isPending || !draft.name.trim() || draft.items.length === 0} onClick={() => save.mutate(draft)}>
              Sačuvaj pečat
            </Button>
            <Button variant="secondary" onClick={() => { setDraft(null); setErr(null); }}>
              Otkaži
            </Button>
          </div>
        </div>
      )}
      {!draft && err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
    </div>
  );
}
